import { autoUpdate, computePosition, flip, offset as offsetMw, shift } from "@floating-ui/dom";
import { isRef, nextTick, onBeforeUnmount, unref, watch } from "vue";

// Keeps `floatingRef` positioned next to `triggerRef` while `isOpen` is true,
// using floating-ui's `flip()` + `shift()` so the floating element stays inside
// the viewport. Uses the fixed strategy and updates on scroll/resize via
// `autoUpdate`. Caller is responsible for `v-if`/mounting the floating element.
// `placement` accepts a string, ref, or getter and is re-read on every update.
export function useFloatingPosition(
  triggerRef,
  floatingRef,
  isOpen,
  { placement = "bottom-start", offset = 4, shiftPadding = 4 } = {},
) {
  let cleanup = null;

  function readPlacement() {
    if (typeof placement === "function") return placement();
    if (isRef(placement)) return unref(placement);
    return placement;
  }

  function stop() {
    cleanup?.();
    cleanup = null;
  }

  watch(isOpen, async (open) => {
    stop();
    if (!open) return;
    await nextTick();
    if (!triggerRef.value || !floatingRef.value) return;
    cleanup = autoUpdate(triggerRef.value, floatingRef.value, async () => {
      if (!triggerRef.value || !floatingRef.value) return;
      const { x, y } = await computePosition(triggerRef.value, floatingRef.value, {
        strategy: "fixed",
        placement: readPlacement(),
        middleware: [offsetMw(offset), flip(), shift({ padding: shiftPadding })],
      });
      if (!triggerRef.value || !floatingRef.value) return;
      Object.assign(floatingRef.value.style, {
        position: "fixed",
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  });

  onBeforeUnmount(stop);
}
